import React from 'react';
import {
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import useAvailablePlans from '../../hooks/subscriptions/useAvailablePlans';
import useGetSubscription from '../../hooks/subscriptions/useGetSubscription';
import { PLANS } from '../../constants/subscriptions';
import { formatDollarAmount, openExternalUrl } from '../../utils/global/helper';
import PlanCardBanner, { BANNER_HEIGHT } from './PlanCardBanner';
import useConfirm from '../../hooks/useConfirm';
import useGetSubscriptionFromStripe from '../../hooks/subscriptions/useGetSubscriptionFromStripe';
import useChangeSubscriptionPlan from '../../hooks/subscriptions/useChangeSubscriptionPlan';
import useUser from '../../hooks/global/useUser';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import { ROUTES, SUPPORT_URL } from '../../constants/global';

interface PlanCardProps {
  plan: (typeof PLANS)[number];
  planIndex: number;
}

function PlanCard({ plan, planIndex }: PlanCardProps) {
  const theme = useTheme();
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const [searchParams] = useSearchParams();
  const upsellPlan = searchParams.get('plan');
  const redirect = searchParams.get('redirect');

  const { user } = useUser();
  const { data: subscriptionData, isError: isErrorSubscription } =
    useGetSubscription();
  const {
    data: stripeData,
    state: { isActive },
    isError: isErrorStripe,
  } = useGetSubscriptionFromStripe();
  const {
    data: availablePlans,
    isLoading: isLoadingPlans,
    isPlaceholderData,
    isError: isErrorAvailablePlans,
  } = useAvailablePlans();
  const { mutate: changeSubscriptionPlan, isPending: isChangingPlan } =
    useChangeSubscriptionPlan(subscriptionData?.subscription?.id!, {
      onSuccess: () => {
        if (
          redirect &&
          Object.values(ROUTES).some(r => r.startsWith(redirect))
        ) {
          window.location.href = redirect;
        } else {
          window.location.reload();
        }
      },
      onError: () => {
        toast.error('Failed to change plan. Please contact support.');
      },
      onMutate: () => {
        openLoadingModal({
          header: 'Changing plan...',
          message:
            'Please wait while we change your plan. This may take a minute...',
        });
      },
      onSettled: () => {
        closeLoadingModal();
      },
    });

  const currentSubscription = subscriptionData?.subscription;
  const currentPlanIndex = PLANS.findIndex(p =>
    p.planIds.includes(currentSubscription?.planId!),
  );

  const isLoadingAvailablePlans = isLoadingPlans || isPlaceholderData;
  const isError =
    isErrorSubscription ||
    isErrorStripe ||
    isErrorAvailablePlans ||
    currentPlanIndex < 0;

  const isCurrentPlan = currentPlanIndex === planIndex;
  const isRecommended =
    !isCurrentPlan &&
    (upsellPlan
      ? upsellPlan === plan.name
      : currentPlanIndex === 0 && planIndex === 1);
  const dbPlan = availablePlans?.find(p => plan.planIds.includes(p.id));
  const isUpgradeOption = planIndex > currentPlanIndex;
  const isDowngradeOption = planIndex < currentPlanIndex;
  const isBannerVisible = isCurrentPlan || isRecommended;
  const contactSupportToDowngrade =
    currentSubscription?.plan.type === 'STUDIO' && plan.type === 'ARTIST';
  const contactSupportToUpgrade = !!stripeData?.coupon;

  const onSupport = React.useCallback(() => {
    openExternalUrl(SUPPORT_URL);
  }, []);

  const onChangePlan = React.useCallback(async () => {
    if (
      isCurrentPlan ||
      (!isUpgradeOption && !isDowngradeOption) ||
      !isActive ||
      !stripeData?.renewsAt ||
      !user
    )
      return;

    if (
      (isUpgradeOption && contactSupportToUpgrade) ||
      (isDowngradeOption && contactSupportToDowngrade)
    ) {
      onSupport();
      return;
    }

    const renewalDate = format(new Date(stripeData.renewsAt), 'MMMM d, yyyy');

    const title = isUpgradeOption
      ? 'Upgrade Your Account?'
      : 'Downgrade Your Account?';

    const priceText = `${formatDollarAmount(
      (dbPlan?.price || 0) / 100,
      true,
    )} per month`;

    const positiveText = isUpgradeOption ? 'Upgrade' : 'Downgrade';

    const confirmed = await confirm({
      title,
      message: `Your account will be ${
        isUpgradeOption ? 'upgraded' : 'downgraded'
      } immediately to the ${
        plan.name
      }. There is no additional charge today, and when your plan renews on ${renewalDate}, you will be charged your new monthly price of ${priceText}`,
      positiveText,
      negativeText: 'Cancel',
    });
    if (!confirmed) return;

    changeSubscriptionPlan({
      businessId: user.business.id,
      planId: plan.defaultPlanId,
    });
  }, [
    isCurrentPlan,
    isUpgradeOption,
    isDowngradeOption,
    isActive,
    stripeData?.renewsAt,
    user,
    contactSupportToUpgrade,
    contactSupportToDowngrade,
    dbPlan?.price,
    confirm,
    plan.name,
    plan.defaultPlanId,
    changeSubscriptionPlan,
    onSupport,
  ]);

  return (
    <Stack
      id={`plan-card-${plan.name}`}
      boxSizing="border-box"
      position="relative"
      key={plan.name}
      flex={1}
      px={2}
      py={3}
      // add extra margin to match when banner is visible and flex is column
      mt={{
        xs:
          planIndex > 0
            ? isBannerVisible
              ? `calc(${theme.spacing(3)} + ${BANNER_HEIGHT}px)`
              : 3
            : 0,
        md: 0,
      }}
      gap={3}
      bgcolor={isCurrentPlan ? theme.palette.lightGrey.main : 'transparent'}
      borderRadius="5px"
      border={
        isCurrentPlan
          ? 'none'
          : `1px solid ${
              isRecommended
                ? theme.palette.secondary.main
                : theme.palette.darkerLightGrey.main
            }`
      }
      sx={{
        borderTopLeftRadius: isBannerVisible ? 0 : '5px',
        borderTopRightRadius: isBannerVisible ? 0 : '5px',
      }}>
      {isCurrentPlan && (
        <PlanCardBanner
          text="Your current plan"
          color="text.secondary"
          border={false}
        />
      )}
      {isRecommended && (
        <PlanCardBanner
          text="Recommended"
          color={theme.palette.secondary.main}
        />
      )}

      <Stack gap={1}>
        <Typography variant="h3">{plan.name}</Typography>
        <Typography variant="body2">{plan.description}</Typography>
      </Stack>

      {isLoadingAvailablePlans || isError ? (
        <Skeleton variant="rounded" height={22} width={100} />
      ) : (
        <Typography variant="h3">
          {formatDollarAmount((dbPlan?.price || 0) / 100, true)} / mo
        </Typography>
      )}

      {!isLoadingAvailablePlans && !isError && (
        <>
          {isUpgradeOption && (
            <Button
              variant={contactSupportToUpgrade ? 'light' : 'base'}
              color={contactSupportToUpgrade ? 'info' : 'secondary'}
              type="button"
              disabled={isChangingPlan}
              onClick={onChangePlan}>
              {contactSupportToUpgrade ? 'Contact support' : plan.upgradeCta}
            </Button>
          )}
          {isDowngradeOption && (
            <Button
              variant="light"
              color="info"
              type="button"
              disabled={isChangingPlan}
              onClick={onChangePlan}>
              {contactSupportToDowngrade
                ? 'Contact support'
                : plan.downgradeCta}
            </Button>
          )}
        </>
      )}

      <Divider
        sx={{
          bgcolor: theme.palette.darkerLightGrey.main,
        }}
      />

      <Stack gap={1}>
        <Typography variant="h5" color="text.secondary">
          {plan.featuresLabel}
        </Typography>
        <List sx={{ p: 0 }}>
          {plan.features.map(feature => (
            <ListItem key={feature} sx={{ p: 0, py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: '24px' }}>
                <CheckIcon sx={{ color: 'text.primary', fontSize: 16 }} />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{
                  variant: 'body2',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Stack>
    </Stack>
  );
}

export default PlanCard;
